import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { dashboardPathForUser } from "@/lib/authRedirect";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Check, Loader2, UserPlus, Link2, X, LogOut } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  coach: "Coach",
  student: "Élève",
  admin: "Admin",
};

const ROLE_BADGE = {
  coach: "bg-blue-100 text-blue-700",
  student: "bg-orange-100 text-orange-700",
  admin: "bg-purple-100 text-purple-700",
};

export function AccountSwitcherSection({ onLogoutCurrent, onLogoutAll }) {
  const { user, displayAccounts, switchAccount, removeAccountFromDevice } = useAuth();
  const navigate = useNavigate();
  const [switchingId, setSwitchingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  if (!user) return null;

  const accounts = displayAccounts?.length
    ? displayAccounts
    : [
        {
          user_id: user.user_id,
          email: user.email,
          name: user.name,
          role: user.role,
          picture: user.picture,
          has_local_token: true,
          is_active: true,
        },
      ];

  const localCount = accounts.filter((a) => a.has_local_token).length;
  const hasMultiple = localCount > 1 || accounts.length > 1;

  const handleSwitch = async (account) => {
    if (account.user_id === user.user_id || switchingId) return;

    if (!account.has_local_token) {
      toast.info(`Reconnectez « ${account.name} » pour l'utiliser sur cet appareil`);
      navigate("/login?add_account=1");
      return;
    }

    setSwitchingId(account.user_id);
    try {
      const next = await switchAccount(account.user_id);
      toast.success(`Compte « ${next.name} » activé`);
      window.location.replace(dashboardPathForUser(next));
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let msg = "Impossible de changer de compte";
      if (typeof raw === "string") {
        if (/more than one result/i.test(raw)) {
          msg = "Erreur serveur temporaire — réessayez dans quelques secondes";
        } else if (raw.toLowerCase().includes("authentifi")) {
          msg = "Session expirée — reconnectez ce compte";
        } else {
          msg = raw;
        }
      }
      toast.error(msg);
      if (typeof raw === "string" && raw.toLowerCase().includes("authentifi")) {
        navigate("/login?add_account=1");
      }
    } finally {
      setSwitchingId(null);
    }
  };

  const handleRemove = async (e, account) => {
    e.stopPropagation();
    if (account.user_id === user.user_id || removingId) return;
    setRemovingId(account.user_id);
    try {
      removeAccountFromDevice(account.user_id);
      toast.success(`« ${account.name} » retiré de cet appareil`);
    } catch (err) {
      toast.error(err.message || "Impossible de retirer ce compte");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <>
      <DropdownMenuLabel className="flex items-center justify-between py-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Mes comptes
        </span>
        {hasMultiple && (
          <Badge variant="secondary" className="text-[10px] h-5 px-1.5">
            {localCount} sur cet appareil
          </Badge>
        )}
      </DropdownMenuLabel>

      {accounts.map((account) => {
        const isActive = account.user_id === user.user_id;
        const isLoading = switchingId === account.user_id;
        const isRemoving = removingId === account.user_id;
        const needsReconnect = !account.has_local_token && !isActive;

        return (
          <DropdownMenuItem
            key={account.user_id}
            onClick={() => handleSwitch(account)}
            disabled={isActive || !!switchingId || isRemoving}
            className="flex items-center gap-3 py-2.5 cursor-pointer group"
            data-testid={`account-switch-${account.user_id}`}
          >
            <div className="relative shrink-0">
              <Avatar className="h-8 w-8">
                <AvatarImage src={account.picture} />
                <AvatarFallback className="bg-blue-100 text-blue-600 text-xs font-semibold">
                  {account.name?.[0]?.toUpperCase() || "?"}
                </AvatarFallback>
              </Avatar>
              {isActive && (
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-blue-600 rounded-full border-2 border-white" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-medium text-slate-900 truncate">{account.name}</p>
                <span
                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0 ${
                    ROLE_BADGE[account.role] || "bg-slate-100 text-slate-600"
                  }`}
                >
                  {ROLE_LABELS[account.role] || account.role}
                </span>
              </div>
              <p className="text-xs text-slate-500 truncate">
                {account.email || ""}
                {needsReconnect ? " · reconnecter" : isActive ? " · actif" : ""}
              </p>
            </div>
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400 shrink-0" />
            ) : isActive ? (
              <Check className="w-4 h-4 text-blue-600 shrink-0" />
            ) : needsReconnect ? (
              <Link2 className="w-4 h-4 text-amber-500 shrink-0" />
            ) : (
              <button
                type="button"
                onClick={(e) => handleRemove(e, account)}
                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-red-500 transition-opacity shrink-0"
                title="Retirer de cet appareil"
                data-testid={`account-remove-${account.user_id}`}
              >
                {isRemoving ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <X className="w-3.5 h-3.5" />
                )}
              </button>
            )}
          </DropdownMenuItem>
        );
      })}

      <DropdownMenuItem
        onClick={() => navigate("/login?add_account=1")}
        className="text-blue-600 cursor-pointer"
        data-testid="add-account-btn"
      >
        <UserPlus className="w-4 h-4 mr-2" />
        Ajouter un compte
      </DropdownMenuItem>

      {hasMultiple && onLogoutCurrent && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={onLogoutCurrent}
            className="text-red-600 cursor-pointer"
            data-testid="logout-current-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Quitter « {user.name} »
          </DropdownMenuItem>
          {onLogoutAll && (
            <DropdownMenuItem
              onClick={onLogoutAll}
              className="text-red-600 cursor-pointer text-xs"
              data-testid="logout-all-btn"
            >
              Tout déconnecter ({localCount} comptes)
            </DropdownMenuItem>
          )}
        </>
      )}

      <DropdownMenuSeparator />
    </>
  );
}